/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import Swal from 'sweetalert2';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDCheckbox from 'components/MDCheckbox';
import MDSelect from 'components/MDSelect';
import TableHeader from 'components/Table/TableHeader';
import TabPanel from 'components/TabPanel/TabPanel';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import nopic from 'assets/images/nopic.jpg';
import SupplierUpload from './SupplierUpload';
import SupplierContact from './SupplierContact';
import SupplierProduct from './SupplierProduct';

const initAttr = {
  id: '',
  code: '',
  name: '',
  taxid: '',
  user_id: '',
  type_id: '',
  credit_day: '',
  credit_money: '',
  tel: '',
  company_name: '',
  email: '',
  business: '',
  vat_id: '2',
  address: '',
  qmin: '',
  lead_time: '',
  review_period: '',
  province_id: '',
  district_id: '',
  subdistrict_id: '',
  zipcode: '',
  branch: '',
  remark: '',
  discount: '',
  country: '',
  credit_type: 'CASH',
  image: nopic,
  thai_country: 'Y',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  user: 'ALL',
  vat: 'ALL',
  province: 'ALL',
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

function Supplier() {
  const mod = 'supplier';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [cboVat, setCboVat] = useState([]);
  const [cboProvince, setCboProvince] = useState([]);
  const [cboDistrict, setCboDistrict] = useState([]);
  const [cboSubDistrict, setCboSubDistrict] = useState([]);
  const [cboUser, setCboUser] = useState([]);
  const [cboType, setCboType] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [tab, setTab] = useState(0);
  const [excelLoading, setExcelLoading] = useState(false);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center' },
    { id: 'tb.code', label: t(`attr.code`), align: 'center' },
    { id: 'tb.name', label: 'ผู้จัดจำหน่าย', align: 'left' },
    { id: 'tb.branch', label: 'สาขา', align: 'left' },
    { id: 'tb.remark', label: 'หมายเหตุ', align: 'left' },
    { id: 'tb.user_id', label: 'ผู้รับผิดชอบ', align: 'left' },
    { id: 'tb.vat_id', label: 'ภาษี', align: 'left' },
    { id: 'tb.credit_day', label: 'เครดิต', align: 'right' },
    { id: 'tb.lead_time', label: 'L/T', align: 'right' },
    { id: 'tb.review_period', label: 'R/P', align: 'right' },
    { id: 'tb.discount', label: 'ส่วนลด', align: 'right' },
    { id: 'tb.province_id', label: 'ที่อยู่', align: 'left' },
    { id: 'tb.enable', label: t(`attr.enable`), align: 'center' },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      role_id: data.role_id,
      user: data.user,
      vat: data.vat,
      province: data.province,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const district = (province_id) => {
    setCboDistrict([]);
    setCboSubDistrict([]);
    FetchData('OPTIONS', `/${mod}/district`, app.token, {
      lang: app.lang,
      province_id: province_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboDistrict(result.district);
      } else {
        Func.exitApp();
      }
    });
  };

  const subDistrict = (district_id) => {
    setCboSubDistrict([]);
    FetchData('OPTIONS', `/${mod}/subdistrict`, app.token, {
      lang: app.lang,
      district_id: district_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboSubDistrict(result.subdistrict);
      } else {
        Func.exitApp();
      }
    });
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setTab(0);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setNavigate(result.navigate);
        district(result.data.province_id, result.data.district_id);
        subDistrict(result.data.district_id);
        setAttr({
          ...result.data,
          tel: Func.maskTel(result.data.tel),
        });

        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.code, 3)) return;
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      code: attr.code.toUpperCase(),
      name: attr.name,
      company_name: attr.company_name,
      taxid: attr.taxid,
      tel: Func.formatTel(attr.tel),
      business: attr.business,
      address: attr.address,
      email: attr.email,
      province_id: attr.province_id,
      subdistrict_id: attr.subdistrict_id,
      district_id: attr.district_id,
      zipcode: attr.zipcode,
      credit_day: attr.credit_day,
      credit_money: attr.credit_money,
      credit_type: attr.credit_type,
      discount: attr.discount,
      enable: attr.enable,
      vat_id: attr.vat_id,
      user_id: attr.user_id,
      type_id: attr.type_id,
      thai_country: attr.thai_country,
      country: attr.country,
      qmin: attr.lead_time * 1 + attr.review_period * 1,
      lead_time: attr.lead_time,
      review_period: attr.review_period,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.code, 3)) return false;
    if (!Func.verifyLength(attr.name, 1)) return false;

    return true;
  };

  const edit = () => {
    if (!editConfirm()) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          code: attr.code.toUpperCase(),
          name: attr.name,
          company_name: attr.company_name,
          taxid: attr.taxid,
          tel: Func.formatTel(attr.tel),
          business: attr.business,
          address: attr.address,
          email: attr.email,
          province_id: attr.province_id,
          subdistrict_id: attr.subdistrict_id,
          district_id: attr.district_id,
          zipcode: attr.zipcode,
          credit_day: attr.credit_day,
          credit_money: attr.credit_money,
          credit_type: attr.credit_type,
          branch: attr.branch,
          enable: attr.enable,
          vat_id: attr.vat_id,
          discount: attr.discount,
          remark: attr.remark,
          user_id: attr.user_id,
          type_id: attr.type_id,
          thai_country: attr.thai_country,
          country: attr.country,
          qmin: attr.lead_time * 1 + attr.review_period * 1,
          lead_time: attr.lead_time,
          review_period: attr.review_period,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            if (id === undefined) view(find);
            else open();

            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const editUser = (item) => {
    setLoading(true);
    setModalUser(false);

    FetchData('PATCH', `/${mod}/user`, app.token, {
      id: attr.id,
      user_id: item.id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);

        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const upload = (filedata) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/upload`, app.token, {
      id: id,
      filedata: filedata,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        open();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboProvince(result.province);
        setCboVat(result.vat);
        setCboUser(result.user);
        setCboType(result.type);
        setUrlExcel(result.url_excel);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const excel = () => {
    setExcelLoading(true);

    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('user', find.user);
    params.append('vat', find.vat);
    params.append('province', find.province);

    const url = `${urlExcel}/download?${params.toString()}`;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;

    iframe.onload = () => {
      setExcelLoading(false);
      document.body.removeChild(iframe);
    };

    iframe.onerror = () => {
      setExcelLoading(false);
      document.body.removeChild(iframe);
    };

    document.body.appendChild(iframe);

    setTimeout(() => {
      setExcelLoading(false);
    }, 10000);
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{
            fontSize: '1rem',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            display: 'inline-block',
          }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const btnUser = (item) => {
    if (!app.perm.supplier_user) return item.user_name;

    if (item.user_id * 1 > 0) {
      return (
        <MDButton
          variant='text'
          color='dark'
          size='small'
          sx={{
            fontSize: '1rem',
            whiteSpace: 'nowrap',
            fontWeight: 'normal',
          }}
          onClick={() => {
            setAttr(item);
            setModalUser(true);
          }}
        >
          {item.user_name}
        </MDButton>
      );
    }

    return (
      <MDButton
        variant='text'
        color='error'
        iconOnly
        sx={{
          fontSize: '0.8rem',
          whiteSpace: 'nowrap',
          fontWeight: 'normal',
        }}
        onClick={() => {
          setAttr(item);
          setModalUser(true);
        }}
      >
        <Icon>person_add_alt</Icon>
      </MDButton>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.user}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, user: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ผู้รับผิดชอบ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboUser.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2}>
            <MDInput
              fullWidth
              value={find.vat}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, vat: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ภาษี :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboVat.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2}>
            <MDInput
              fullWidth
              value={find.province}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, province: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>จังหวัด :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboProvince.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='success' onClick={excel} disabled={excelLoading}>
                {excelLoading ? (
                  <>
                    <CircularProgress size={16} color='inherit' sx={{ mr: 1 }} />
                    Loading...
                  </>
                ) : (
                  <>
                    <Icon>table_view</Icon>&nbsp;Excel
                  </>
                )}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setCboDistrict([]);
                setCboSubDistrict([]);
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left'>{btnOpen(row)}</TableCell>
                      <TableCell>
                        <MDBox width={200}>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.branch}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.remark}
                      </TableCell>
                      <TableCell align='left'>{btnUser(row)}</TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.vat_name}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        <MDBox>{row.credit_day === '0' ? '-' : `${row.credit_day} วัน`}</MDBox>
                        <MDBox>
                          {row.credit_money === '0.00'
                            ? '-'
                            : `${Func.numberDisplay(row.credit_money)} ฿`}
                        </MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        {row.lead_time === '0.00' ? '-' : Func.numberDisplay(row.lead_time)}
                      </TableCell>
                      <TableCell align='right'>
                        {row.review_period === '0.00' ? '-' : Func.numberDisplay(row.review_period)}
                      </TableCell>
                      <TableCell align='right'>
                        {row.discount > 0 ? <span>{row.discount}%</span> : <span>-</span>}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.thai_country === 'Y' ? row.province_name : row.country}
                      </TableCell>
                      <TableCell align='center'>{Func.enableDisplay(row.enable)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderImage = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          Image
        </MDTypography>
        <MDBox>
          {attr.image === '' ? null : (
            <MDBox component='img' src={attr.image} alt={attr.name} width='100%' height='100%' />
          )}
          <MDBox mr={1} display='flex' justifyContent='center'>
            <SupplierUpload uploadData={upload} />
          </MDBox>
        </MDBox>
      </Paper>
    );
  };

  const renderFormInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          Information
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {attr.image === '' ? null : (
              <MDBox component='img' src={attr.image} alt={attr.image} width='100%' height='100%' />
            )}
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รหัสผู้จัดจำหน่าย *'
              onChange={(e) => setAttr({ ...attr, code: e.target.value })}
              value={attr.code}
              variant='standard'
              inputProps={{
                maxLength: 10,
                autoComplete: 'off',
                sx: { textTransform: 'uppercase' },
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อผู้จัดจำหน่าย *'
              onChange={(e) => setAttr({ ...attr, name: e.target.value })}
              value={attr.name}
              multiline
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label={`${t(`attr.enable`)}`}
              checked={attr.enable === 'Y'}
              onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormCompany = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          ข้อมูลบริษัท
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อบริษัท'
              onChange={(e) => setAttr({ ...attr, company_name: e.target.value })}
              value={attr.company_name}
              multiline
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='สาขา'
              onChange={(e) => setAttr({ ...attr, branch: e.target.value })}
              value={attr.branch}
              type='email'
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ประเภทธุรกิจ'
              onChange={(e) => setAttr({ ...attr, business: e.target.value })}
              value={attr.business}
              variant='standard'
              multiline
              inputProps={{
                maxLength: 255,
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='ประเภทผู้จัดจำหน่าย *'
              labelDefault={t(`element.please_select`)}
              data={cboType}
              value={attr.type_id}
              onChange={(e) => setAttr({ ...attr, type_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.taxid`)}`}
              onChange={(e) => setAttr({ ...attr, taxid: Func.maskIdCard(e.target.value) })}
              value={attr.taxid}
              variant='standard'
              fullWidth
              type='search'
              inputProps={{
                autoComplete: 'off',
                maxLength: 17,
                placeholder: '0-0000-00000-00-0',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.tel`)}`}
              onChange={(e) => setAttr({ ...attr, tel: Func.maskTel(e.target.value) })}
              value={attr.tel}
              variant='standard'
              fullWidth
              type='search'
              inputProps={{
                autoComplete: 'off',
                maxLength: 14,
                placeholder: '(000) 000 0000',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.email`)}`}
              onChange={(e) => setAttr({ ...attr, email: e.target.value })}
              value={attr.email}
              type='email'
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='ภาษี'
              labelDefault={t(`element.please_select`)}
              data={cboVat}
              value={attr.vat_id}
              onChange={(e) => setAttr({ ...attr, vat_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='ผู้รับผิดชอบ'
              labelDefault={t(`element.please_select`)}
              data={cboUser}
              value={attr.user_id}
              onChange={(e) => setAttr({ ...attr, user_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormAddress = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          ที่อยู่
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDSelect
              label='ประเทศ'
              labelDefault={t(`element.please_select`)}
              data={[
                { id: 'Y', name: 'ไทย' },
                { id: 'N', name: 'ต่างประเทศ' },
              ]}
              value={attr.thai_country}
              onChange={(e) => setAttr({ ...attr, thai_country: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          {attr.thai_country === 'Y' ? null : (
            <Grid item xs={12}>
              <MDInput
                label='ระบุประเทศ'
                onChange={(e) => setAttr({ ...attr, country: e.target.value })}
                value={attr.country}
                variant='standard'
                multiline
                inputProps={{
                  autoComplete: 'off',
                }}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.address`)}`}
              onChange={(e) => setAttr({ ...attr, address: e.target.value })}
              value={attr.address}
              variant='standard'
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          {attr.thai_country === 'N' ? null : (
            <Grid item xs={12}>
              <MDSelect
                label='จังหวัด'
                labelDefault={t(`element.please_select`)}
                data={cboProvince}
                value={attr.province_id}
                onChange={(e) => {
                  setAttr({ ...attr, province_id: e.target.value, zipcode: '' });
                  district(e.target.value);
                }}
                inputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
          )}
          {attr.thai_country === 'N' ? null : (
            <Grid item xs={12}>
              <MDSelect
                label='อำเภอ / เขต'
                labelDefault={t(`element.please_select`)}
                data={cboDistrict}
                value={attr.district_id}
                onChange={(e) => {
                  setAttr({ ...attr, district_id: e.target.value, zipcode: '' });
                  subDistrict(e.target.value);
                }}
                inputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
          )}
          {attr.thai_country === 'N' ? null : (
            <Grid item xs={12}>
              <MDSelect
                label='ตำบล / แขวง'
                labelDefault={t(`element.please_select`)}
                data={cboSubDistrict}
                value={attr.subdistrict_id}
                onChange={(e) => {
                  cboSubDistrict.forEach((item, i) => {
                    if (item.id === e.target.value) {
                      setAttr({ ...attr, subdistrict_id: e.target.value, zipcode: item.zip_code });
                    }
                  });
                }}
                inputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
          )}
          {attr.thai_country === 'N' ? null : (
            <Grid item xs={12}>
              <MDInput
                label='รหัสไปรษณีย์'
                onChange={(e) => setAttr({ ...attr, zipcode: e.target.value })}
                value={attr.zipcode}
                type='number'
                variant='standard'
                inputProps={{
                  autoComplete: 'off',
                }}
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </MDBox>
    );
  };

  const renderFormCredit = () => {
    return (
      <MDBox className='card' p={2}>
        <MDTypography variant='h5' mb={3}>
          Credit
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl variant='standard' fullWidth>
              <InputLabel>ประเภท</InputLabel>
              <Select
                value={attr.credit_type}
                onChange={(e) => setAttr({ ...attr, credit_type: e.target.value })}
              >
                <MenuItem value='CASH'>เงินสด</MenuItem>
                <MenuItem value='CREDIT'>เงินเชื่อ</MenuItem>
                <MenuItem value='CONSIGNMENT'>ฝากขาย</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='เครดิตวัน'
              onChange={(e) => setAttr({ ...attr, credit_day: e.target.value })}
              value={attr.credit_day}
              type='number'
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position='end'>วัน</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='เครดิตวงเงิน'
              onChange={(e) => setAttr({ ...attr, credit_money: e.target.value })}
              value={attr.credit_money}
              type='number'
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position='end'>บาท</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='Lead time'
              onChange={(e) => setAttr({ ...attr, lead_time: e.target.value })}
              value={attr.lead_time}
              type='number'
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position='end'>วัน</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='Review period'
              onChange={(e) => setAttr({ ...attr, review_period: e.target.value })}
              value={attr.review_period}
              type='number'
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position='end'>วัน</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ส่วนลด'
              onChange={(e) => setAttr({ ...attr, discount: e.target.value })}
              value={attr.discount}
              type='number'
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='หมายเหตุ'
              onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
              value={attr.remark}
              multiline
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='ชื่อผู้จัดจำหน่าย *'
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='ชื่อบริษัท'
            onChange={(e) => setAttr({ ...attr, company_name: e.target.value })}
            value={attr.company_name}
            multiline
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='ธุรกิจ'
            onChange={(e) => setAttr({ ...attr, business: e.target.value })}
            value={attr.business}
            variant='standard'
            multiline
            inputProps={{
              maxLength: 200,
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='รหัส *'
            onChange={(e) => setAttr({ ...attr, code: e.target.value })}
            value={attr.code}
            variant='standard'
            inputProps={{
              maxLength: 10,
              autoComplete: 'off',
              sx: { textTransform: 'uppercase' },
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.taxid`)}`}
            onChange={(e) => setAttr({ ...attr, taxid: Func.maskIdCard(e.target.value) })}
            value={attr.taxid}
            variant='standard'
            fullWidth
            type='search'
            inputProps={{
              autoComplete: 'off',
              maxLength: 17,
              placeholder: '0-0000-00000-00-0',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label='ประเภทผู้จัดจำหน่าย *'
            labelDefault={t(`element.please_select`)}
            data={cboType}
            value={attr.type_id}
            onChange={(e) => setAttr({ ...attr, type_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.tel`)}`}
            onChange={(e) => setAttr({ ...attr, tel: Func.maskTel(e.target.value) })}
            value={attr.tel}
            variant='standard'
            fullWidth
            type='search'
            inputProps={{
              autoComplete: 'off',
              maxLength: 14,
              placeholder: '(000) 000 0000',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.email`)}`}
            onChange={(e) => setAttr({ ...attr, email: e.target.value })}
            value={attr.email}
            type='email'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={`${t(`attr.address`)}`}
            onChange={(e) => setAttr({ ...attr, address: e.target.value })}
            value={attr.address}
            variant='standard'
            multiline
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='จังหวัด'
            labelDefault={t(`element.please_select`)}
            data={cboProvince}
            value={attr.province_id}
            onChange={(e) => {
              setAttr({ ...attr, province_id: e.target.value, zipcode: '' });
              district(e.target.value);
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='อำเภอ / เขต'
            labelDefault={t(`element.please_select`)}
            data={cboDistrict}
            value={attr.district_id}
            onChange={(e) => {
              setAttr({ ...attr, district_id: e.target.value, zipcode: '' });
              subDistrict(e.target.value);
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='ตำบล / แขวง'
            labelDefault={t(`element.please_select`)}
            data={cboSubDistrict}
            value={attr.subdistrict_id}
            onChange={(e) => {
              // setAttr({ ...attr, subdistrict_id: e.target.value });

              cboSubDistrict.forEach((item, i) => {
                if (item.id === e.target.value) {
                  setAttr({ ...attr, subdistrict_id: e.target.value, zipcode: item.zip_code });
                }
              });
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='รหัสไปรษณีย์'
            onChange={(e) => setAttr({ ...attr, zipcode: e.target.value })}
            value={attr.zipcode}
            type='number'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MDInput
            label='เครดิตวัน'
            onChange={(e) => setAttr({ ...attr, credit_day: e.target.value })}
            value={attr.credit_day}
            type='number'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MDInput
            label='เครดิตวงเงิน'
            onChange={(e) => setAttr({ ...attr, credit_money: e.target.value })}
            value={attr.credit_money}
            type='number'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MDInput
            label='รอบการเติม'
            onChange={(e) => setAttr({ ...attr, qmin: e.target.value })}
            value={attr.qmin}
            type='number'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='ภาษี'
            labelDefault={t(`element.please_select`)}
            data={cboVat}
            value={attr.vat_id}
            onChange={(e) => setAttr({ ...attr, vat_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='ส่วนลด'
            onChange={(e) => setAttr({ ...attr, discount: e.target.value })}
            value={attr.discount}
            type='number'
            variant='standard'
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position='end'>%</InputAdornment>,
              inputProps: {
                className: 'text-right',
                autoComplete: 'off',
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDCheckbox
            label='Enable'
            checked={attr.enable === 'Y'}
            onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='md' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        <MDButton variant='gradient' color='success' onClick={edit}>
          <Icon>save</Icon>&nbsp;{t(`element.save`)}
        </MDButton>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='tabs-addedit'>
                    <Tab label={t(`element.info`)} />
                    <Tab label='สินค้า' />
                    <Tab label='ผู้ติดต่อ' />
                    <Tab label={t(`attr.image`)} />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <MDTypography variant='h3' mb={3}>
                        {attr.name}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderButton()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      {renderFormInfo()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      {renderFormCompany()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      {renderFormAddress()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      {renderFormCredit()}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <MDBox className='card'>
                    <SupplierProduct />
                  </MDBox>
                </TabPanel>
                <TabPanel value={tab} index={2}>
                  <MDBox className='card'>
                    <SupplierContact />
                  </MDBox>
                </TabPanel>
                <TabPanel value={tab} index={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={5} lg={3}>
                      {renderImage()}
                    </Grid>
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderUser = () => {
    return (
      <Dialog onClose={() => setModalUser(false)} open={modalUser}>
        <DialogTitle>เลือกผู้รับผิดชอบ {attr.code}</DialogTitle>
        <List sx={{ pt: 0 }}>
          {cboUser.map((row) => (
            <ListItem disablePadding key={row.id}>
              <ListItemButton onClick={() => editUser(row)}>
                <ListItemAvatar>
                  <Avatar src={row.image} />
                </ListItemAvatar>
                <ListItemText primary={row.name} />
              </ListItemButton>
            </ListItem>
          ))}

          <ListItem disablePadding>
            <ListItemButton autoFocus onClick={() => editUser({ id: '0' })}>
              <ListItemAvatar>
                <Avatar>
                  <Icon>close</Icon>
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary='ไม่มีผู้รับผิดชอบ' />
            </ListItemButton>
          </ListItem>
        </List>
      </Dialog>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.purchase`), t(`menu.supplier`)]} />
      {renderPage()}
      {renderAdd()}
      {renderUser()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Supplier;
